import React from 'react';
import { Link } from 'react-router-dom';
import { useScrollReveal } from '../hooks/useScrollReveal';

const About: React.FC = () => {
  const scrollRef = useScrollReveal();

  return (
    <div className="about-page" ref={scrollRef}>
      {/* Hero */}
      <section className="about-hero reveal-section">
        <div className="container" style={{ paddingTop: '7rem', paddingBottom: '4rem' }}>
          <span className="eyebrow reveal">About Us</span>
          <h1 className="reveal" style={{ fontSize: 'clamp(2rem, 5vw, 3.2rem)', fontWeight: 700, lineHeight: 1.12, letterSpacing: '-0.03em', color: 'var(--text)', maxWidth: '700px' }}>
            Protecting the creative work of Somali content creators
          </h1>
          <p className="reveal" style={{ fontSize: '1.1rem', lineHeight: 1.75, color: 'var(--text-secondary)', maxWidth: '640px', marginTop: '1.5rem' }}>
            Somali Copyright Agency Ltd. is a licensed copyright enforcement service built specifically for the Somali creator community.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="reveal-section" style={{ padding: '4rem 0 5rem', background: 'var(--surface)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4rem', alignItems: 'start' }}>
            <div className="reveal">
              <span className="eyebrow">Our Story</span>
              <h2 style={{ fontSize: '1.8rem', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text)', marginBottom: '1.5rem', lineHeight: 1.2 }}>
                Born from a real problem
              </h2>
              <p style={{ fontSize: '1rem', lineHeight: 1.8, color: 'var(--text-secondary)', marginBottom: '1.25rem' }}>
                Somali creators are some of the most talented and fastest-growing content producers on TikTok, Facebook, and YouTube. But with that growth came a problem — content theft became rampant. Videos were being copied, re-uploaded, and monetized by people who had no right to them.
              </p>
              <p style={{ fontSize: '1rem', lineHeight: 1.8, color: 'var(--text-secondary)', marginBottom: '1.25rem' }}>
                Creators were losing real money. Some had their entire channels copied. And there was no one helping them — no affordable service, no one who understood the platforms they used, and no one who spoke their language.
              </p>
              <p style={{ fontSize: '1rem', lineHeight: 1.8, color: 'var(--text-secondary)' }}>
                We built Somali Copyright Agency to fill that gap. A real, licensed company that handles DMCA takedowns professionally — at a price creators can actually afford.
              </p>
            </div>
            <div className="reveal" style={{ background: 'var(--accent-light)', borderRadius: 'var(--radius)', padding: '2.5rem', border: '1px solid var(--border)' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)', marginBottom: '1.5rem' }}>Company Details</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                <div>
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.2rem' }}>Registered Name</p>
                  <p style={{ fontWeight: 600, color: 'var(--text)' }}>Somali Copyright Agency Ltd.</p>
                </div>
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: '1.25rem' }}>
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.2rem' }}>License Number</p>
                  <p style={{ fontWeight: 600, color: 'var(--text)' }}>0295800</p>
                </div>
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: '1.25rem' }}>
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.2rem' }}>Service Type</p>
                  <p style={{ fontWeight: 600, color: 'var(--text)' }}>DMCA Takedown &amp; Copyright Documentation</p>
                </div>
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: '1.25rem' }}>
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.2rem' }}>Compliance</p>
                  <p style={{ fontWeight: 600, color: 'var(--text)' }}>DMCA &amp; Somalia Copyright Law No. 66 of 1977</p>
                </div>
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: '1.25rem' }}>
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.2rem' }}>Platforms Covered</p>
                  <p style={{ fontWeight: 600, color: 'var(--text)' }}>TikTok, Facebook, YouTube, Instagram</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="reveal-section" style={{ padding: '5rem 0', background: 'var(--canvas)' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <span className="eyebrow reveal">What We Do</span>
          <h2 className="reveal" style={{ fontSize: 'clamp(1.6rem, 3.5vw, 2.4rem)', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text)', marginBottom: '3rem', lineHeight: 1.15 }}>
            End-to-end copyright protection
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1px', background: 'var(--border)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden', maxWidth: '900px', margin: '0 auto' }}>
            <div className="reveal" style={{ background: 'var(--surface)', padding: '2rem 1.5rem', textAlign: 'center' }}>
              <div style={{ width: 40, height: 40, borderRadius: 'var(--radius-sm)', background: 'var(--accent-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" /><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" /></svg>
              </div>
              <h3 style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text)', marginBottom: '0.5rem' }}>Document</h3>
              <p style={{ fontSize: '0.85rem', lineHeight: 1.6, color: 'var(--text-secondary)' }}>Preserve timestamps, metadata, and proof of ownership</p>
            </div>
            <div className="reveal" style={{ background: 'var(--surface)', padding: '2rem 1.5rem', textAlign: 'center' }}>
              <div style={{ width: 40, height: 40, borderRadius: 'var(--radius-sm)', background: 'var(--accent-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" /></svg>
              </div>
              <h3 style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text)', marginBottom: '0.5rem' }}>File</h3>
              <p style={{ fontSize: '0.85rem', lineHeight: 1.6, color: 'var(--text-secondary)' }}>Submit DMCA notices directly to platform legal teams</p>
            </div>
            <div className="reveal" style={{ background: 'var(--surface)', padding: '2rem 1.5rem', textAlign: 'center' }}>
              <div style={{ width: 40, height: 40, borderRadius: 'var(--radius-sm)', background: 'var(--accent-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><path d="M22 4L12 14.01l-3-3" /></svg>
              </div>
              <h3 style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text)', marginBottom: '0.5rem' }}>Enforce</h3>
              <p style={{ fontSize: '0.85rem', lineHeight: 1.6, color: 'var(--text-secondary)' }}>Follow up until content is removed and rights restored</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="reveal-section" style={{ padding: '5rem 0', background: 'var(--surface)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
        <div className="container" style={{ textAlign: 'center', maxWidth: '720px' }}>
          <span className="eyebrow reveal">Our Mission</span>
          <p className="reveal" style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', fontSize: 'clamp(1.4rem, 3vw, 2rem)', lineHeight: 1.5, color: 'var(--text)', marginBottom: '2rem' }}>
            Every Somali creator deserves to own their work and profit from it — without theft, without exploitation, and without needing a lawyer to fight back.
          </p>
          <p className="reveal" style={{ fontSize: '1rem', lineHeight: 1.8, color: 'var(--text-secondary)' }}>
            We believe copyright protection should not be a luxury reserved for those who can afford legal representation. Our mission is to make professional DMCA enforcement accessible, affordable, and effective for every content creator in the Somali community — whether they have 100 followers or 1 million.
          </p>
        </div>
      </section>

      {/* Values */}
      <section className="reveal-section" style={{ padding: '5rem 0', background: 'var(--canvas)' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <span className="eyebrow reveal">Our Values</span>
          <h2 className="reveal" style={{ fontSize: 'clamp(1.6rem, 3.5vw, 2.4rem)', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text)', marginBottom: '3rem', lineHeight: 1.15 }}>
            What drives us
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem', maxWidth: '800px', margin: '0 auto' }}>
            {[
              { title: 'Creator First', body: 'Every decision we make starts with one question: does this help creators protect their work? If not, we don\'t do it.' },
              { title: 'Full Transparency', body: 'No hidden fees. No confusing legal jargon. You always know what\'s happening with your case and what it costs.' },
              { title: 'Real Results', body: 'We don\'t just send letters. We follow through, track progress, and keep pushing until stolen content is removed.' },
              { title: 'Community Driven', body: 'Built by people who understand the Somali creator ecosystem. We know the platforms, the culture, and the challenges.' },
            ].map((v) => (
              <div className="reveal" key={v.title} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '2rem', textAlign: 'left' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)', marginBottom: '0.5rem' }}>{v.title}</h3>
                <p style={{ fontSize: '0.9rem', lineHeight: 1.65, color: 'var(--text-secondary)' }}>{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="reveal-section" style={{ padding: '5rem 0', background: 'var(--primary)', color: 'white', textAlign: 'center' }}>
        <div className="container">
          <h2 className="reveal" style={{ fontSize: 'clamp(1.6rem, 3.5vw, 2.4rem)', fontWeight: 700, letterSpacing: '-0.02em', marginBottom: '1rem', lineHeight: 1.15 }}>
            Ready to protect your content?
          </h2>
          <p className="reveal" style={{ color: 'rgba(255,255,255,0.65)', marginBottom: '2rem', fontSize: '1.05rem' }}>
            File a DMCA takedown in 5 minutes. We handle the rest.
          </p>
          <div className="reveal">
            <Link to="/contact" className="btn-primary" style={{ background: 'white', color: 'var(--primary)' }}>
              Report Stolen Content <span className="btn-arrow">&rarr;</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
